import { getFileUrl, uploadFile } from '../../../api/images'
import { getSourceImage } from '../../../utils/sourceImageDb'
import type { GenerationCard } from '../../../types/generationCard'

export interface UploadedFile {
	file: File
	preview: string
	url?: string
	/** Референс «под капотом» из админ-карточки (19.09): канал занят, но в payload уходит
	 *  ФЛАГ (card_ref_video / card_ref_audio), а файл подставляет сервер по source_card_id.
	 *  Ссылка здесь — короткая подпись только на просмотр, в генерацию она не едет. */
	cardRef?: boolean
	/** Что сделали с фото при подгонке под нейросеть (imagePrepare): «увеличили до 300×420 px» и т. п. —
	 *  пометка у превью. Нет — фото ушло как есть. */
	note?: string
	/** Фото положила сцена (персонаж, локация, предмет — useSceneApply), а не юзер: зеркало именованных слотов
	 *  карточки такие фото не стирает. */
	sceneAsset?: boolean
	/** Восстановлено из снимка localStorage (GenerateContext: generatePersistedUploads), а не положено юзером сейчас:
	 *  файла нет, только ссылка. «Повторить движение» такие не считает своим фото в слот (14.10). */
	restored?: boolean
}

export interface UploadedVideo extends UploadedFile {
	duration?: number
	/** Секунды входа, которые сервер получит ровно такими (плата за секунды входного видео): «Повторить движение» перед
	 *  запуском режет ролик до отрезка этой длины (motion/motionTool.clipNeedsCut), поэтому вход — отрезок, а не исходник
	 *  по ссылке. Есть — цену со входом считаем по ним; нет — секунды входа меряет сервер, как у всех. */
	billedSeconds?: number
}

export type FileCategory = 'images' | 'videos' | 'audio'

export async function uploadAndGetUrl(file: File, type: FileCategory): Promise<string> {
	const uploadResult = await uploadFile(file, type)
	const urlResult = await getFileUrl(uploadResult.id)
	return urlResult.url
}

/** Точная длина ролика в секундах (25,04 — так и вернём): округление — забота потребителя.
 *  Раньше здесь стоял Math.ceil, и 25,04 с превращались в 26 ещё до пилюли (разбор 25.09). */
export async function getVideoDuration(file: File): Promise<number> {
	return new Promise<number>((resolve) => {
		const url = URL.createObjectURL(file)
		const video = document.createElement('video')
		video.preload = 'metadata'
		video.onloadedmetadata = () => {
			URL.revokeObjectURL(url)
			resolve(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 5)
		}
		video.onerror = () => {
			URL.revokeObjectURL(url)
			resolve(5)
		}
		video.src = url
	})
}

export function revokePreviewUrl(preview: string): void {
	if (preview?.startsWith('blob:')) {
		URL.revokeObjectURL(preview)
	}
}

/**
 * Загружает изображение по URL и возвращает File.
 * Пробует прямой fetch (работает для same-origin и CORS-совместимых URL),
 * при ошибке — canvas-подход с crossOrigin='anonymous'.
 * Если оба способа не работают — возвращает null.
 */
export async function fetchImageAsFile(url: string, filename = 'image'): Promise<File | null> {
	// data: URL — fetch работает без CORS
	// remote URL — пробуем fetch, потом canvas
	try {
		const response = await fetch(url)
		if (!response.ok) throw new Error('HTTP error')
		const blob = await response.blob()
		const type = blob.type || 'image/jpeg'
		const ext = type.includes('png') ? 'png'
			: type.includes('webp') ? 'webp'
			: type.includes('mp4') ? 'mp4'
			: type.includes('webm') ? 'webm'
			: type.includes('mov') || type.includes('quicktime') ? 'mov'
			: type.includes('avi') ? 'avi'
			: type.startsWith('video/') ? 'mp4'
			: type.includes('mpeg') && type.startsWith('audio/') ? 'mp3'
			: type.includes('wav') ? 'wav'
			: type.includes('ogg') ? 'ogg'
			: type.includes('aac') ? 'aac'
			: type.includes('mp4') && type.startsWith('audio/') ? 'm4a'
			: type.startsWith('audio/') ? 'mp3'
			: 'jpg'
		return new File([blob], `${filename}.${ext}`, { type })
	} catch {
		// Fallback: canvas с crossOrigin='anonymous'
		return new Promise((resolve) => {
			const img = new Image()
			img.crossOrigin = 'anonymous'
			img.onload = () => {
				const canvas = document.createElement('canvas')
				canvas.width = img.naturalWidth
				canvas.height = img.naturalHeight
				const ctx = canvas.getContext('2d')
				if (!ctx) return resolve(null)
				ctx.drawImage(img, 0, 0)
				canvas.toBlob(
					(blob) => {
						if (!blob) return resolve(null)
						resolve(new File([blob], `${filename}.jpg`, { type: 'image/jpeg' }))
					},
					'image/jpeg',
					0.92,
				)
			}
			img.onerror = () => resolve(null)
			img.src = url
		})
	}
}

/**
 * Получает исходный файл из карточки генерации.
 * Сначала пробует IndexedDB (новые записи), потом base64 sourceImageUrl (старые записи).
 * Возвращает null если исходника нет (text-to-image генерация).
 */
export async function getSourceFileFromCard(card: GenerationCard): Promise<File | null> {
	// Новые записи: источник в IndexedDB
	if (card.sourceImageDbKey) {
		try {
			const file = await getSourceImage(card.sourceImageDbKey)
			if (file) return file
		} catch {
			// fallback к base64
		}
	}
	// Старые записи: источник в base64 строке
	if (card.sourceImageUrl) {
		return fetchImageAsFile(card.sourceImageUrl, 'reuse')
	}
	return null
}
